// Bid Updates — the running conversation on a bid before it is a job: "do you have this
// bid item?", "so-and-so wants a price on this", "bid date moved". Posting pings the
// bid's PM; the app posts its own note when the bid date or time changes. On award the
// same rows read as the job's Daily Updates, so nothing is retyped.
//
// Its own file (like DocumentsPillow): JobPillows imports from AdminBidDetail, and the
// bid page needs this too, so living in JobPillows would make an import cycle.
import React, { useCallback, useEffect, useState } from 'react';
import Reactions from '../../components/Reactions';
import { apiFetch } from '../../services/apiClient';

const when = s => {
  if (!s) return '';
  const d = new Date(s);
  return isNaN(d) ? '' : d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};
const muted = { fontSize: 12, color: '#9ca3af' };
// Who's signed in — so a person can edit what they wrote.
const meId = () => { try { return String(JSON.parse(localStorage.getItem('titan_session') || 'null')?.user?.id ?? ''); } catch { return ''; } };

export const announceBidUpdates = () => window.dispatchEvent(new CustomEvent('titan:bid-updates-changed'));

export default function BidUpdatesPillow({ bidId, pmName, canPost = true, canRemove = false, compact = false }) {
  const [list, setList] = useState(null);
  const [text, setText] = useState('');
  const [asBanner, setAsBanner] = useState(false);   // 📌 also a banner across the bid page (addendums — Oct 9 2026)
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);   // { id, text }
  const me = meId();
  const canEditRow = u => u.kind !== 'system' && (canRemove || String(u.author_id) === me);
  async function saveEdit() {
    if (!editing || !editing.text.trim()) return;
    try {
      await apiFetch(`/daily-updates/${editing.id}`, { method: 'PATCH', body: JSON.stringify({ notes: editing.text.trim() }) });
      setEditing(null); await load(); announceBidUpdates();
    } catch { /* keep the editor open so nothing is lost */ }
  }

  const load = useCallback(() => {
    if (!bidId) return;
    apiFetch(`/bids/${bidId}/daily-updates`).then(r => setList(Array.isArray(r) ? r : [])).catch(() => setList([]));
  }, [bidId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    window.addEventListener('titan:bid-updates-changed', load);
    return () => window.removeEventListener('titan:bid-updates-changed', load);
  }, [load]);

  async function post() {
    const notes = text.trim();
    if (!notes || busy) return;
    setBusy(true);
    try {
      await apiFetch(`/bids/${bidId}/daily-updates`, { method: 'POST', body: JSON.stringify({ notes, banner: asBanner }) });
      setText(''); setAsBanner(false);
      await load();
      announceBidUpdates();
    } catch { /* leave the text alone so nothing is lost */ }
    setBusy(false);
  }
  async function setBanner(u, on) {
    try { await apiFetch(`/bids/${bidId}/daily-updates/${u.id}/banner`, { method: 'PATCH', body: JSON.stringify({ on }) }); await load(); announceBidUpdates(); } catch {}
  }
  async function remove(u) {
    if (!window.confirm('Remove this update?')) return;
    try { await apiFetch(`/bids/${bidId}/daily-updates/${u.id}`, { method: 'DELETE' }); await load(); announceBidUpdates(); } catch {}
  }

  if (list === null) return <div style={muted}>Loading…</div>;
  const shown = compact ? list.slice(0, 3) : list;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {canPost && !compact && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <textarea value={text} onChange={e => setText(e.target.value)} rows={2}
            onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) post(); }}
            placeholder={pmName ? `Ask ${pmName} something, or note what happened — "Do we have a price on the pull boxes?"` : 'Ask the PM something, or note what happened…'}
            style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #e5e7eb', borderRadius: 8, padding: '9px 11px', fontSize: 13, resize: 'vertical', fontFamily: 'inherit' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button onClick={post} disabled={!text.trim() || busy}
              style={{ padding: '8px 18px', borderRadius: 9, border: 'none', background: text.trim() ? '#1e3a5f' : '#cbd5e1', color: '#fff', fontSize: 13, fontWeight: 800, cursor: text.trim() ? 'pointer' : 'default' }}>
              {busy ? 'Posting…' : 'Post'}
            </button>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 700, color: asBanner ? '#b91c1c' : '#374151', cursor: 'pointer' }}>
              <input type="checkbox" checked={asBanner} onChange={e => setAsBanner(e.target.checked)} /> 📌 Show as a banner on this bid
            </label>
            <span style={muted}>{pmName ? `${pmName} gets a notification.` : 'The bid’s PM gets a notification once one is assigned.'} Ctrl+Enter posts.</span>
          </div>
        </div>
      )}

      {shown.length === 0 && <div style={{ fontSize: 13, color: '#9ca3af' }}>No updates on this bid yet.</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {shown.map(u => {
          const sys = u.kind === 'system';
          return (
            <div key={u.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 10px', borderRadius: 8, border: `1px solid ${Number(u.banner) === 1 && !u.banner_off_at ? '#f87171' : sys ? '#fde68a' : '#e5e7eb'}`, background: Number(u.banner) === 1 && !u.banner_off_at ? '#fef2f2' : sys ? '#fffbeb' : '#fff' }}>
              <span style={{ fontSize: 14, lineHeight: 1.3 }}>{sys ? '📅' : '💬'}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                {editing && editing.id === u.id ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <textarea autoFocus value={editing.text} onChange={e => setEditing(ed => ({ ...ed, text: e.target.value }))} rows={2}
                      onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) saveEdit(); if (e.key === 'Escape') setEditing(null); }}
                      style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #d1d5db', borderRadius: 7, padding: '7px 9px', fontSize: 13, resize: 'vertical', fontFamily: 'inherit' }} />
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={saveEdit} disabled={!editing.text.trim()} style={{ padding: '5px 14px', borderRadius: 7, border: 'none', background: '#1e3a5f', color: '#fff', fontSize: 12.5, fontWeight: 800, cursor: 'pointer' }}>Save</button>
                      <button onClick={() => setEditing(null)} style={{ padding: '5px 12px', borderRadius: 7, border: '1px solid #e5e7eb', background: '#fff', fontSize: 12.5, color: '#6b7280', cursor: 'pointer' }}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ fontSize: 13, color: '#111', whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.4 }}>{u.notes}</div>
                )}
                <div style={{ ...muted, marginTop: 3 }}>{u.author_name || 'Someone'} · {when(u.created_at)}{u.edited_at ? ' · edited' : ''}
                  {/* a banner post: up on the bid page, or taken down — either can be changed here */}
                  {Number(u.banner) === 1 && !compact && (!u.banner_off_at
                    ? <> · <b style={{ color: '#b91c1c' }}>📌 banner</b> <button onClick={() => setBanner(u, false)} style={{ background: 'none', border: 'none', padding: 0, color: '#b91c1c', fontSize: 12, fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}>take down</button></>
                    : <> · 📌 banner taken down <button onClick={() => setBanner(u, true)} style={{ background: 'none', border: 'none', padding: 0, color: '#1d4ed8', fontSize: 12, fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}>put back up</button></>)}
                </div>
                {!sys && <Reactions type="update" id={u.id} reactions={u.reactions} />}
              </div>
              {canEditRow(u) && !(editing && editing.id === u.id) && (
                <button onClick={() => setEditing({ id: u.id, text: u.notes })} title="Edit" style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: 13, cursor: 'pointer', lineHeight: 1 }}>✎</button>
              )}
              {canRemove && !sys && (
                <button onClick={() => remove(u)} title="Remove" style={{ background: 'none', border: 'none', color: '#c9ccd1', fontSize: 16, cursor: 'pointer', lineHeight: 1 }}>×</button>
              )}
            </div>
          );
        })}
      </div>
      {compact && list.length > 3 && <div style={muted}>{list.length - 3} more…</div>}
    </div>
  );
}
